import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../LanguageContext";
import { useSettings } from "../SettingsContext";
import { catLabel } from "../brand";
import EditableI18n from "../components/EditableI18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATS = ["all", "lashes", "brows", "iv", "fillers", "botox"];

// Uploads coming from the backend live at /api/uploads/xxx and need the
// backend origin prepended. Full https:// URLs are passed through as-is.
const resolveImg = (u) => {
  if (!u) return "";
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
};

export default function Gallery() {
  const { t, lang } = useLang();
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/gallery`)
      .then((r) => setItems(r.data.gallery || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

  return (
    <main data-testid="gallery-page" className="pt-32 min-h-screen">
      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-20">
        <p className="qw-overline mb-6">{t.gallery.title}</p>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-foreground mb-6 text-balance max-w-3xl">
          <EditableI18n k="gallery.title" label="Gallery title" />
        </h1>
        <p className="text-base lg:text-lg text-muted-foreground max-w-2xl"><EditableI18n k="gallery.subtitle" label="Gallery subtitle" /></p>
      </section>

      <div className="qw-divider mx-6 md:mx-12 lg:mx-20" />

      <section className="px-6 md:px-12 lg:px-20 py-12">
        <div className="flex flex-wrap gap-3 mb-12">
          {CATS.map((c) => (
            <button
              key={c}
              data-testid={`gallery-filter-${c}`}
              onClick={() => setFilter(c)}
              className={`px-5 py-2 text-[0.7rem] tracking-[0.25em] uppercase border transition-all ${
                filter === c
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-transparent text-foreground/80 border-border hover:border-primary hover:text-primary"
              }`}
            >
              {c === "all" ? (lang === "fr" ? "Tout" : "All") : catLabel(settings, lang, c, t.services.categories[c])}
            </button>
          ))}
        </div>

        {!loaded ? (
          <p className="text-muted-foreground text-center py-20">…</p>
        ) : filtered.length === 0 ? (
          <p data-testid="gallery-empty" className="text-muted-foreground text-center py-20 border-y border-border">
            {t.gallery.empty}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((g) => (
              <div key={g.id} data-testid={`gallery-item-${g.id}`} className="group">
                <div className="grid grid-cols-2 gap-px bg-border mb-4 aspect-square">
                  <div className="relative overflow-hidden bg-muted">
                    <img src={resolveImg(g.before_url)} alt="before" className="w-full h-full object-cover" />
                    <span className="absolute top-3 left-3 qw-overline bg-background/80 px-2 py-1 text-foreground">{t.gallery.before}</span>
                  </div>
                  <div className="relative overflow-hidden bg-muted">
                    <img src={resolveImg(g.after_url)} alt="after" className="w-full h-full object-cover" />
                    <span className="absolute top-3 left-3 qw-overline bg-primary text-primary-foreground px-2 py-1">{t.gallery.after}</span>
                  </div>
                </div>
                <p className="font-serif text-xl text-foreground">{lang === "fr" ? g.title_fr : g.title_en}</p>
                <p className="text-xs text-muted-foreground tracking-wider uppercase mt-1">{catLabel(settings, lang, g.category, t.services.categories[g.category])}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
